import { ArrowRightIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { useMemo } from 'react';

import Skeleton from '@/components/UI/Skeleton';
import Footer from '@/renderer/Footer';
import Header from '@/renderer/Header';
import { useLocale } from '@/renderer/utils/locale';
import useRequest from '@/renderer/utils/useRequest';
import type { RequestSchema } from '@/types/request';

const getStats: RequestSchema<API.ReleaseStats> = {
  input: '/api/v1/release/stats',
};

export default PicksArchivePage;

const baseStats = {
  win: 0,
  loss: 0,
  pending: 0,
  push: 0,
  canceled: 0,
  noAction: 0,
  risk: 0,
  units: 0,
  total: 0,
};

export function PicksArchive() {
  const { formatNumber } = useLocale();

  const nflStatsRequest = useRequest(
    getStats,
    useMemo(
      () => ({
        query: {
          page: { limit: 100 },
          data: {
            playType: ['side', 'total', 'prop', 'future', 'not-available'],
          },
        },
      }),
      [],
    ),
  );

  const isLoading = nflStatsRequest.loading;

  const stats = useMemo(() => {
    const results: Record<
      API.Id,
      {
        cumulative: API.ReleaseStats['seasonId'][1];
        seasons: API.ReleaseStats['seasonId'];
        firstSeasonId: API.Id;
        lastSeasonId: API.Id;
        firstSeasonDate: number;
        lastSeasonDate: number;
        last4Ids: API.Id[];
        last4: API.ReleaseStats['seasonId'][1][];
        seasonsMeta: Record<API.Id, Partial<API.Season>>;
        sportMeta: Partial<API.Sport>;
      }
    > = {};

    if (nflStatsRequest?.data?.seasonId) {
      for (const seasonKey in nflStatsRequest.data.seasonId) {
        const season = nflStatsRequest.data.seasonId[seasonKey];
        let sportId = season.meta.sport?.[0]?.id;
        let seasonId = season.meta.season?.[0]?.id;
        const seasonStats = season.stats;
        let sportMeta = {};

        if (season.meta.sport) {
          sportMeta = Object.values(season.meta.sport)[0];
          sportId = sportMeta?.id;
        }

        if (sportId !== 1 && sportId !== 2 && sportId !== 3) {
          continue; // we only care about CFB, CBB, NFL
        }

        if (!sportId || sportId < 1) {
          continue; // skip these ones
        }

        // Initialize result set by sportId
        if (!results[sportId]) {
          results[sportId] = {
            cumulative: { ...baseStats },
            seasons: {},
            firstSeasonId: Infinity,
            lastSeasonId: -Infinity,
            firstSeasonDate: Infinity,
            lastSeasonDate: -Infinity,
            last4Ids: [],
            last4: [],
            seasonsMeta: {},
            sportMeta,
          };
        }

        const sportRef = results[sportId];

        let seasonDate = 0;

        if (season.meta.season) {
          const seasonMeta = Object.values(season.meta.season)[0];

          if (seasonMeta) {
            seasonId = seasonMeta.id;

            if (!seasonId || seasonId < 1) {
              continue; // skip these ones
            }

            seasonDate = Date.parse(seasonMeta.startDate || seasonMeta.endDate);

            sportRef.seasonsMeta[seasonId] = seasonMeta;
          }
        }

        if (!seasonId || seasonId < 1) {
          continue; // skip these ones
        }

        // Initialize result set season stats inside sportId
        if (!sportRef.seasons[seasonId]) {
          sportRef.seasons[seasonId] = { ...baseStats };
        }

        const seasonRef = sportRef.seasons[seasonId];

        let islast4 = false;

        // is this one of the last 4 seasons?
        if (sportRef.last4Ids.length < 4) {
          sportRef.last4Ids.push(seasonId);

          sportRef.last4Ids.sort((aSeasonId, bSeasonId) => {
            const aSeasonDate = Date.parse(
              sportRef.seasonsMeta[aSeasonId].startDate || sportRef.seasonsMeta[aSeasonId].endDate,
            );
            const bSeasonDate = Date.parse(
              sportRef.seasonsMeta[bSeasonId].startDate || sportRef.seasonsMeta[bSeasonId].endDate,
            );
            return aSeasonDate > bSeasonDate ? 1 : aSeasonDate < bSeasonDate ? -1 : 0;
          });

          sportRef.last4.push({ ...baseStats });

          islast4 = true;
        } else if (
          Date.parse(
            sportRef.seasonsMeta[sportRef.last4Ids[0]].startDate ||
              sportRef.seasonsMeta[sportRef.last4Ids[0]].endDate,
          ) < seasonDate
        ) {
          sportRef.last4Ids.shift();
          sportRef.last4Ids.push(seasonId);

          sportRef.last4.shift();

          sportRef.last4.push({ ...baseStats });

          const joinedSeasons = sportRef.last4Ids
            .map((seasonId, idx) => ({ seasonId: seasonId, values: sportRef.last4[idx] }))
            .sort(({ seasonId: aSeasonId }, { seasonId: bSeasonId }) => {
              const aSeasonDate = Date.parse(
                sportRef.seasonsMeta[aSeasonId].startDate ||
                  sportRef.seasonsMeta[aSeasonId].endDate,
              );
              const bSeasonDate = Date.parse(
                sportRef.seasonsMeta[bSeasonId].startDate ||
                  sportRef.seasonsMeta[bSeasonId].endDate,
              );
              return aSeasonDate > bSeasonDate ? 1 : aSeasonDate < bSeasonDate ? -1 : 0;
            });

          sportRef.last4Ids = joinedSeasons.map((item) => item.seasonId);
          sportRef.last4 = joinedSeasons.map((item) => item.values);

          islast4 = true;
        }

        // Is this the first or last season?
        if (seasonDate > sportRef.lastSeasonDate) {
          sportRef.lastSeasonDate = seasonDate;
          sportRef.lastSeasonId = seasonId;
        }

        if (seasonDate < sportRef.firstSeasonDate) {
          sportRef.firstSeasonDate = seasonDate;
          sportRef.firstSeasonId = seasonId;
        }

        for (const result in seasonStats) {
          switch (result) {
            case 'win':
            case 'loss':
            case 'pending':
            case 'push':
            case 'noAction':
            case 'canceled': {
              const resultStats = seasonStats[result];

              seasonRef[result] += resultStats.total;
              sportRef.cumulative[result] += resultStats.total;

              seasonRef.risk += season.risk;
              seasonRef.units += season.units;
              seasonRef.total += season.total;

              if (islast4) {
                sportRef.last4[sportRef.last4.length - 1][result] += resultStats.total;

                sportRef.last4[sportRef.last4.length - 1].risk += resultStats.risk;
                sportRef.last4[sportRef.last4.length - 1].units += resultStats.units;
                sportRef.last4[sportRef.last4.length - 1].total += resultStats.total;
              }
            }
          }
        }

        sportRef.cumulative.risk += season.risk;
        sportRef.cumulative.units += season.units;
        sportRef.cumulative.total += season.total;
      }

      return results;
    }

    return undefined;
  }, [nflStatsRequest?.data]);

  const blocks = useMemo(() => {
    const results = {};

    for (const sportId of [1, 2, 3]) {
      const sportStats = stats?.[sportId];

      const currentSeasonId = sportStats?.last4Ids[sportStats?.last4Ids.length - 1];
      const currentSeason = sportStats?.last4[sportStats?.last4.length - 1];
      const lastSeasonId = sportStats?.last4Ids[sportStats?.last4Ids.length - 2];
      const lastSeason = sportStats?.last4[sportStats?.last4.length - 2];

      const sportPrev3Ids = sportStats?.last4Ids ? sportStats.last4Ids.slice(0, 3) : [];
      const sportPrev3 = sportStats?.last4 ? sportStats.last4.slice(0, 3) : [];

      if (sportPrev3Ids.length === sportStats?.last4Ids.length) {
        // not enough seasons, remove one more
        sportPrev3Ids.length -= 1;
        sportPrev3.length -= 1;
      }

      const sportLast3Cumulative = sportPrev3?.reduce((obj, season) => {
        for (const result in season) {
          if (obj[result]) {
            obj[result] += season[result];
          } else {
            obj[result] = season[result];
          }
        }

        return obj;
      }, {});

      results[sportId] = [
        [[currentSeasonId], currentSeason, 'Current season'],
        [[lastSeasonId], lastSeason, 'Last season'],
        [sportPrev3Ids, sportLast3Cumulative, `Last ${sportPrev3Ids.length} seasons`],
        [[sportStats?.firstSeasonId, sportStats?.lastSeasonId], sportStats?.cumulative, 'All time'],
      ];
    }

    return results;
  }, [stats]);

  return (
    <div className="flex h-40 min-h-[stretch] flex-1 grow flex-col justify-between overflow-hidden">
      <Header title="All Results" className="py-2 md:hidden" />
      <div className="mx-auto w-full overflow-auto px-4 py-8 sm:px-6 lg:px-8">
        <div className="mx-auto w-full max-w-7xl">
          <div className="px-4 sm:px-6 lg:px-8">
            <div className="relative mx-auto max-w-2xl pb-8 md:pb-24 md:pt-20 text-center">
              <h1 className="text-3xl hidden md:block font-bold tracking-tight text-zinc-900 dark:text-zinc-200 sm:text-4xl">
                All Results
              </h1>
              <p className="mt-4 text-left md:text-center text-base leading-7 text-zinc-600 dark:text-zinc-400">
                All records are graded against a conservative widely available consensus line from
                sharp, market-leading sportsbooks and not until most common PPH skins have posted.
                No openers, rogues, overnights, or stale lines are ever used. Since 2020, all RAS
                results have been independently monitored by{' '}
                <a href="https://betstamp.app/u/RAS" target="_blank" rel="noreferrer">
                  betstamp
                </a>
                .
              </p>
            </div>
          </div>
          {/* <a href="https://betstamp.app/u/RAS" target="_blank" rel="noreferrer">
            <div className="rounded bg-sky-50 p-4 text-sm text-sky-800 ring-sky-900/10 hover:text-sky-900 dark:bg-sky-900/50 dark:text-sky-200 dark:hover:text-sky-100">
              <div className="flex space-x-2 items-center">
                <CheckBadgeIcon className="size-6 shrink-0 text-sky-500" />
                <div>
                  <span>Since 2020, all RAS results have been independently monitored by</span>
                  <img
                    src={srcBetstamp}
                    className="mx-auto inline-block ml-1 h-6 relative shrink-0 pb-px"
                  />
                  <span>.</span>
                </div>
              </div>
            </div>
          </a> */}
          {[3, 1, 2].map((sportId, idx) => {
            const sportMeta = stats?.[sportId]?.sportMeta;

            return (
              <section className="mt-12" key={`${sportId}-${idx}`}>
                <div className="flex flex-row items-baseline justify-between">
                  <div className="text-lg/7 font-semibold tracking-[-0.015em] text-zinc-950 sm:text-2xl/7 dark:text-white">
                    <Skeleton loading={isLoading}>{sportMeta?.name || 'Loading...'}</Skeleton>
                  </div>
                  {/* <a
                    className="flex flex-row items-center space-x-2 rounded bg-zinc-900 px-2.5 py-1.5 text-sm text-white hover:bg-zinc-700 hover:text-white dark:bg-zinc-500"
                    href={sportId ? `/archive?sportId=${sportId}` : '#'}
                  >
                    <span>{sportMeta?.name || '(Loading...)'} results</span>
                  </a> */}
                </div>

                <aside className="mt-4 grid w-full grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
                  {blocks[sportId].map(([seasonIds, nums, title], idx) => {
                    const firstSeasonMeta = stats?.[sportId]?.seasonsMeta[seasonIds[0]];
                    const lastSeasonMeta =
                      stats?.[sportId]?.seasonsMeta[seasonIds[seasonIds.length - 1]];
                    const lastSeasonNameParts =
                      `${lastSeasonMeta?.name || firstSeasonMeta?.name || ''}`.split('-');
                    const lastSeasonName = lastSeasonNameParts[1] || lastSeasonNameParts[0];
                    const [firstSeasonName] =
                      `${firstSeasonMeta?.name || lastSeasonMeta?.name || ''}`.split('-');

                    return (
                      <a
                        className="group rounded bg-gradient-to-t hover:shadow-sm from-zinc-900/2.5 hover:from-zinc-900/5 to-transparent p-4 sm:p-6 sm:py-8 text-left font-semibold ring-1 ring-zinc-900/10 dark:bg-white/5 dark:ring-white/20 text-zinc-950 dark:text-white hover:text-zinc-950 dark:hover:text-white dark:hover:bg-white/10"
                        key={`${firstSeasonMeta?.id}-${lastSeasonMeta?.id}-${idx}`}
                        href={
                          firstSeasonMeta || lastSeasonMeta
                            ? `/archive?sportId=${sportId}&startDate=${`${firstSeasonMeta?.startDate || ''}`.split('T')[0].replace(/-/g, '')}&endDate=${`${lastSeasonMeta?.endDate || ''}`.split('T')[0].replace(/-/g, '')}&playType=full`
                            : '#'
                        }
                      >
                        <div className="flex flex-row items-center justify-between">
                          <p className="text-lg font-normal">{title}</p>
                          <ChevronRightIcon
                            className="size-5 shrink-0 group-hover:hidden"
                            aria-hidden="true"
                          />
                          <ArrowRightIcon
                            className="size-5 shrink-0 hidden group-hover:inline-block"
                            aria-hidden="true"
                          />
                        </div>
                        <span className="pb-4 text-sm font-normal uppercase tracking-wider text-zinc-500 dark:text-zinc-300">
                          <Skeleton loading={isLoading}>
                            {(firstSeasonName === lastSeasonName
                              ? firstSeasonName
                              : `${firstSeasonName}-${lastSeasonName}`) || 'Loading...'}
                          </Skeleton>
                        </span>
                        <div className="grid grid-cols-2 sm:grid-cols-1 lg:grid-cols-2 xl:grid-cols-1 gap-2 sm:gap-4 mt-4 sm:mt-6">
                          <div className="flex flex-col text-base">
                            <span className="text-xs font-normal text-zinc-500 dark:text-zinc-400">
                              Record
                            </span>
                            <span className="text-xl">
                              <Skeleton loading={isLoading}>
                                {nums?.win || 0}-{nums?.loss || 0}-{nums?.push || 0}
                              </Skeleton>
                            </span>
                          </div>
                          <div className="flex flex-col text-base">
                            <span className="text-xs font-normal text-zinc-500 dark:text-zinc-400">
                              Win rate
                            </span>
                            <span className="text-xl">
                              <Skeleton loading={isLoading}>
                                {formatNumber(
                                  (nums?.win || 0) / ((nums?.win || 0) + (nums?.loss || 0)) || 0,
                                  {
                                    style: 'percent',
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                  },
                                )}
                              </Skeleton>
                            </span>
                          </div>
                          <div className="flex flex-col text-base">
                            <span className="text-xs font-normal text-zinc-500 dark:text-zinc-400">
                              Units
                            </span>
                            <span
                              className="text-xl text-emerald-600 dark:text-emerald-400 data-[negative=true]:text-red-600 data-[negative=true]:dark:text-red-400"
                              data-negative={nums?.units < 0}
                            >
                              <Skeleton loading={isLoading}>
                                {nums?.units < 0 ? '' : '+'}
                                {formatNumber(nums?.units || 0, {
                                  style: 'decimal',
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2,
                                })}
                              </Skeleton>
                            </span>
                          </div>
                        </div>
                      </a>
                    );
                  })}
                </aside>
              </section>
            );
          })}
        </div>
        <Footer />
      </div>
    </div>
  );
}

// Main component
function PicksArchivePage() {
  return <PicksArchive />;
}
